import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useLocation } from "react-router";
import { useAngemeldet } from "../../kern/sitzung";
import { anzeigename } from "../../kern/namen";
import { personNach, type PersonKurz } from "../../kern/personen";
import { tagMitWochentag } from "../../kern/kalender";
import { heute, tageBis } from "../../kern/zeit";
import { fehlertext } from "../../kern/fehler";
import { Avatar } from "../../kern/ui/Avatar";
import { Bestaetigung } from "../../kern/ui/Bestaetigung";
import { useHinweis } from "../../kern/ui/Hinweise";
import { useAufgabenAktionen, useKommentare, useVerlauf, type Kommentar } from "./daten";
import { erwaehnteIds, erwaehnungAmCursor, textMitErwaehnungen, verlaufText, type Aufgabe } from "./logik";
import d from "./dialog.module.css";

type Filter = "alle" | "kommentare" | "verlauf";
const FILTER: Record<Filter, string> = { alle: "Alle", kommentare: "Kommentare", verlauf: "Verlauf" };

const UHR = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });

/** „heute 14:05“, „gestern“, „Mo. 12.10.“ (Berliner Zeit) */
export function zeitLabel(iso: string, heuteTag = heute()): string {
  const tag = heute(new Date(iso));
  const n = tageBis(tag, heuteTag);
  if (n === 0) return `heute ${UHR.format(new Date(iso))}`;
  if (n === -1) return "gestern";
  return tagMitWochentag(tag);
}

interface Eintrag {
  art: "kommentar" | "verlauf";
  id: string;
  zeit: string;
  kommentar?: Kommentar;
  text?: string;
  wer?: string | null;
}

interface Props {
  aufgabe: Pick<Aufgabe, "id" | "titel" | "sichtbarkeit" | "erstellt_von" | "personen">;
  personen: readonly PersonKurz[] | undefined;
  /** Grund, warum keine neuen Kommentare gehen (Archiv, gelöscht), sonst null */
  gesperrt: string | null;
  /** Nach dem Anlegen: Fokus ins Kommentarfeld */
  fokus?: boolean | undefined;
}

/** Rechte Spalte im Dialog (AUFGABEN-STUFE2.md, Abschnitt 4.2): Kommentare und Verlauf in einer Zeitleiste, älteste oben,
 *  Eingabefeld darunter. Mit @ erwähnt man jemanden. Neu laden beim Öffnen und alle 30 Sekunden. */
export function Aktivitaet({ aufgabe, personen, gesperrt, fokus }: Props) {
  const { person: ich } = useAngemeldet();
  const ort = useLocation();
  const { zeigen } = useHinweis();
  const kommentare = useKommentare(aufgabe.id);
  const verlauf = useVerlauf(aufgabe.id);
  const akt = useAufgabenAktionen();
  const liste = useRef<HTMLDivElement>(null);
  const feld = useRef<HTMLTextAreaElement>(null);
  const [filter, setFilter] = useState<Filter>("alle");
  const [text, setText] = useState("");
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [erwaehnung, setErwaehnung] = useState<{ start: number; suche: string } | null>(null);
  const [markiert, setMarkiert] = useState(0);
  const [loeschen, setLoeschen] = useState<Kommentar | null>(null);
  const angesprungen = useRef<string | null>(null);
  const gescrollt = useRef(0);

  const name = (id: string | null | undefined) => (id ? anzeigename(personNach(personen, id)) : "Jemand");
  const vorname = (id: string | null | undefined) => personNach(personen, id)?.vorname || name(id);
  const vornamen = useMemo(() => (personen ?? []).map((p) => p.vorname).filter(Boolean), [personen]);

  // Wer die Aufgabe sieht: bei „Nur Vorstand“ der Vorstand, die Verantwortlichen und wer sie angelegt hat. Die Datenbank prüft noch einmal.
  const kandidaten = (personen ?? []).filter(
    (p) => p.aktiv && p.id !== ich.id && (aufgabe.sichtbarkeit === "mitglieder" || p.vorstand || aufgabe.personen.includes(p.id) || p.id === aufgabe.erstellt_von),
  );
  const treffer = erwaehnung
    ? kandidaten.filter((p) => (p.vorname || p.name).toLocaleLowerCase("de").startsWith(erwaehnung.suche.toLocaleLowerCase("de"))).slice(0, 8)
    : [];

  const eintraege: Eintrag[] = [
    ...(kommentare.data ?? []).map((k) => ({ art: "kommentar" as const, id: k.id, zeit: k.erstellt_am, kommentar: k })),
    ...(verlauf.data ?? [])
      .filter((e) => e.typ !== "aufgaben.kommentiert" && (filter === "verlauf" || e.typ !== "aufgaben.kommentar_entfernt"))
      .map((e) => ({
        art: "verlauf" as const,
        id: String(e.id),
        zeit: e.erstellt_am,
        wer: e.person,
        text: verlaufText(e.typ, (e.daten ?? {}) as Record<string, unknown>, e.person ? vorname(e.person) : "System", (pid) => vorname(pid), e.person),
      })),
  ]
    .filter((e) => filter === "alle" || (filter === "kommentare" ? e.art === "kommentar" : e.art === "verlauf"))
    .sort((a, b) => a.zeit.localeCompare(b.zeit));
  const anzahlKommentare = (kommentare.data ?? []).filter((k) => !k.geloescht_am).length;

  // Ans Ende scrollen, wenn Neues dazukommt. Mit #k-<id> stattdessen zum Kommentar und kurz hervorheben.
  const geladen = kommentare.isSuccess && verlauf.isSuccess;
  const anzahl = eintraege.length;
  useEffect(() => {
    if (!geladen) return;
    const ziel = ort.hash.startsWith("#k-") ? ort.hash.slice(3) : null;
    if (ziel && angesprungen.current !== ziel) {
      const el = document.getElementById(`k-${ziel}`);
      if (el) {
        angesprungen.current = ziel;
        gescrollt.current = anzahl;
        el.scrollIntoView({ block: "center" });
        const klasse = d.hervor ?? "hervor";
        el.classList.add(klasse);
        window.setTimeout(() => el.classList.remove(klasse), 2500);
        return;
      }
    }
    if (liste.current && gescrollt.current !== anzahl) {
      gescrollt.current = anzahl;
      liste.current.scrollTop = liste.current.scrollHeight;
    }
  }, [geladen, anzahl, ort.hash]);

  useEffect(() => {
    if (fokus) feld.current?.focus();
  }, [fokus]);

  function eingabe(wert: string, cursor: number) {
    setText(wert);
    setErwaehnung(erwaehnungAmCursor(wert, cursor));
    setMarkiert(0);
  }

  function uebernehmen(p: PersonKurz) {
    if (!erwaehnung) return;
    const vor = text.slice(0, erwaehnung.start);
    const nach = text.slice(erwaehnung.start + 1 + erwaehnung.suche.length);
    const einsatz = `@${p.vorname || p.name} `;
    const neu = vor + einsatz + nach.replace(/^\s/, "");
    setText(neu);
    setGewaehlt((g) => (g.includes(p.id) ? g : [...g, p.id]));
    setErwaehnung(null);
    requestAnimationFrame(() => {
      const pos = vor.length + einsatz.length;
      feld.current?.focus();
      feld.current?.setSelectionRange(pos, pos);
    });
  }

  function senden() {
    const t = text.trim();
    if (!t || akt.kommentieren.isPending) return;
    const erwaehnt = erwaehnteIds(t, kandidaten.map((p) => ({ id: p.id, vorname: p.vorname || p.name })), gewaehlt);
    akt.kommentieren.mutate(
      { id: aufgabe.id, text: t, erwaehnt },
      {
        onSuccess: () => {
          setText("");
          setGewaehlt([]);
          const namen = erwaehnt.map((pid) => vorname(pid));
          zeigen(
            namen.length === 0
              ? "Kommentar gesendet."
              : `${namen.length === 1 ? namen[0] : `${namen.slice(0, -1).join(", ")} und ${namen.at(-1)}`} ${namen.length === 1 ? "bekommt" : "bekommen"} eine Mitteilung.`,
          );
        },
        onError: (e) => zeigen(fehlertext(e), { fehler: true }),
      },
    );
  }

  function taste(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (erwaehnung && treffer.length > 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setMarkiert((m) => (m + (e.key === "ArrowDown" ? 1 : -1) + treffer.length) % treffer.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const p = treffer[markiert];
        if (p) uebernehmen(p);
        return;
      }
    }
    if (erwaehnung && e.key === "Escape") {
      // Nur die Liste schließen, nicht den Dialog
      e.preventDefault();
      e.stopPropagation();
      setErwaehnung(null);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      senden();
    }
  }

  return (
    <div className={d.aktivitaet}>
      <div className={d.aktKopf}>
        <h3 id="aktivitaet-titel">Aktivität</h3>
        <div className={d.filter} role="group" aria-label="Aktivität filtern">
          {(Object.keys(FILTER) as Filter[]).map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {FILTER[f]}
              {f === "kommentare" && anzahlKommentare > 0 ? <span className={d.zahl}> {anzahlKommentare}</span> : null}
            </button>
          ))}
        </div>
      </div>
      <div ref={liste} className={d.zeitleiste} aria-labelledby="aktivitaet-titel" role="log">
        {kommentare.isError ? <p className={d.meta}>{fehlertext(kommentare.error)}</p> : null}
        {geladen && eintraege.length === 0 ? <p className={d.meta}>Noch nichts passiert.</p> : null}
        <ol className={d.eintraege}>
          {eintraege.map((e) =>
            e.art === "kommentar" && e.kommentar ? (
              <li key={`k-${e.id}`} id={`k-${e.id}`} className={d.kommentar}>
                <Avatar person={personNach(personen, e.kommentar.person)} groesse={34} />
                <div className={d.kommentarKarte}>
                  <p className={d.kommentarKopf}>
                    <strong>{vorname(e.kommentar.person)}</strong> <span className={d.meta}>{zeitLabel(e.zeit)}</span>
                  </p>
                  {e.kommentar.geloescht_am ? (
                    <p className={d.entfernt}>Kommentar entfernt</p>
                  ) : (
                    <p className={d.kommentarText}>
                      {textMitErwaehnungen(e.kommentar.text, vornamen).map((t, i) =>
                        t.erwaehnt ? (
                          <span key={i} className={d.erwaehnt}>
                            {t.text}
                          </span>
                        ) : (
                          <span key={i}>{t.text}</span>
                        ),
                      )}
                    </p>
                  )}
                  {e.kommentar.person === ich.id && !e.kommentar.geloescht_am && !gesperrt ? (
                    <button type="button" className={d.leiseKnopf} onClick={() => setLoeschen(e.kommentar ?? null)}>
                      Löschen<span className="nur-leser"> (eigener Kommentar)</span>
                    </button>
                  ) : null}
                </div>
              </li>
            ) : (
              <li key={`v-${e.id}`} className={d.verlaufEintrag}>
                <span className={d.punkt} aria-hidden="true" />
                <span>{e.text}</span>
                <span className={d.meta}>{zeitLabel(e.zeit)}</span>
              </li>
            ),
          )}
        </ol>
      </div>
      <div className={d.eingabeBereich}>
        {gesperrt ? (
          <p className={d.meta}>{gesperrt}</p>
        ) : (
          <>
            {erwaehnung && treffer.length > 0 ? (
              <ul className={d.erwaehnListe} role="listbox" aria-label="Person erwähnen" id="erwaehnen-liste">
                {treffer.map((p, i) => (
                  <li key={p.id} role="option" aria-selected={i === markiert}>
                    <button type="button" tabIndex={-1} className={i === markiert ? d.markiert : undefined} onMouseDown={(ev) => ev.preventDefault()} onClick={() => uebernehmen(p)}>
                      <Avatar person={p} groesse={28} />
                      <span>{p.vorname || anzeigename(p)}</span>
                      <span className={d.meta}>{p.vorstand ? "Vorstand" : "Mitglied"}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <label className="nur-leser" htmlFor="kommentar-feld">
              Kommentar
            </label>
            <textarea
              ref={feld}
              id="kommentar-feld"
              className={d.kommentarFeld}
              value={text}
              maxLength={5000}
              placeholder="Schreib einen Kommentar … Mit @ erwähnst du jemanden."
              aria-autocomplete="list"
              aria-controls={erwaehnung && treffer.length > 0 ? "erwaehnen-liste" : undefined}
              aria-expanded={!!erwaehnung && treffer.length > 0}
              onChange={(ev) => eingabe(ev.target.value, ev.target.selectionStart)}
              onKeyDown={taste}
              onBlur={() => window.setTimeout(() => setErwaehnung(null), 150)}
            />
            <div className={d.eingabeFuss}>
              <span className={d.meta}>Enter sendet, Shift + Enter für eine neue Zeile</span>
              <button type="button" className={d.senden} disabled={!text.trim() || akt.kommentieren.isPending} onClick={senden}>
                Senden
              </button>
            </div>
          </>
        )}
      </div>
      <Bestaetigung
        offen={!!loeschen}
        titel="Kommentar löschen?"
        bestaetigen="Löschen"
        gefaehrlich
        laeuft={akt.kommentarLoeschen.isPending}
        onAbbrechen={() => setLoeschen(null)}
        onBestaetigen={() =>
          loeschen &&
          akt.kommentarLoeschen.mutate(
            { id: aufgabe.id, kommentar: loeschen.id },
            { onSuccess: () => setLoeschen(null), onError: (e) => zeigen(fehlertext(e), { fehler: true }) },
          )
        }
      >
        <p>An seiner Stelle steht dann „Kommentar entfernt“. Bearbeiten geht nicht.</p>
      </Bestaetigung>
    </div>
  );
}
